import { mpClient } from "./client";
import { remotePlayers } from "./remote-players";
import { mpSnapshotBuffer } from "./snapshot-buffer";
import { mpCanvasToNorm } from "../../../lib/multiplayer/normal-sim";
import type { MpGameMode, MpSandboxConfigWire } from "../../../lib/multiplayer/protocol";
import {
  drawEquippedCosmeticOverlays,
  equippedHidesBaseCursor,
  filterPlayCosmeticIds,
} from "../cursor-cosmetics";
import { setHighwayRandom } from "../highway-mode";
import { gameRandom } from "../game-rng";
import {
  beginMpHighwayClient,
  drawMpHighwayClient,
  isMpHighwayClientActive,
  mpHighwayCheckCollision,
  mpHighwayClientScore,
  mpHighwayCursorOnCanvas,
  mpHighwayWorldSpawn,
  resetMpHighwayClient,
  tickMpHighwayClient,
} from "./mp-highway";
import {
  resetMpSpeedrunSpectate,
  setRemoteSpeedrunPoints,
  shouldHideMpLocalCursor,
  shouldDrawRemoteMpCursors,
  shouldDrawRemoteMpTrails,
  isMpSpeedrunSpectating,
  getMpSpectateCursorCanvas,
  addMpSpeedrunFinisher,
  applyTeamSpeedrunPoints,
  applyCoopTeamSpeedrunPointsFromServer,
  respawnMpSpeedrunFromLaser,
  isMpSpeedrunCoop,
} from "./mp-speedrun-spectate";
import { drawMpSnakeTrails, drawMpSnakePlayBorder, resetMpSnakeTrails, tickLocalMpSnakeTrail } from "./mp-snake-trails";

export {
  resetMpSpeedrunSpectate,
  markMpSpeedrunLocalFinished,
  isMpSpeedrunLocalFinished,
  getMpSpeedrunLocalFinishTime,
  getMpSpeedrunFinishers,
  syncLocalSpeedrunPoints,
  isMpSpeedrunSpectating,
  cycleMpSpectateTarget,
  getMpSpectateTargetName,
  getMpSpectatePointsSet,
  getMpSpectateCursorCanvas,
  hideMpSpeedrunInGameChrome,
  isMpSpeedrunCoop,
  isMpSpeedrunRace,
  getMpSpeedrunAuthTick,
  getMpSpeedrunSimTimeSec,
  registerMpSpeedrunCoopHooks,
  respawnMpSpeedrunFromLaser,
} from "./mp-speedrun-spectate";

export type MpGameHooks = {
  initGame: (mode: string) => void;
  showEl: (el: HTMLElement | null) => void;
  hideEl: (el: HTMLElement | null) => void;
  getCanvasSize: () => { w: number; h: number };
  getMouse: () => { x: number; y: number };
  setMouse: (x: number, y: number) => void;
  isPlaying: () => boolean;
  getGameMode: () => string | null;
  setTopHudText: (text: string) => void;
  endMultiplayerGame: () => void;
  releaseGamePointer: () => void;
  getTrailEnabled: () => boolean;
  getTrailColor: () => string;
  prepareMpModeGame: (
    mode: MpGameMode,
    opts: { speedrunLevel?: number | null; sandboxConfig?: MpSandboxConfigWire | null },
  ) => void;
  encodeMpPos: (
    mode: MpGameMode,
    mouse: { x: number; y: number },
    cw: number,
    ch: number,
  ) => { x: number; y: number };
  getMpLocalCursor: (cw: number, ch: number) => { x: number; y: number };
  getCursorRadiusPx: () => number;
  startMpGameCountdown: () => void;
  isMpGameplayInputBlocked: () => boolean;
  playModeCardBeep: () => void;
  clearLocalTrail?: () => void;
};

let hooks: MpGameHooks | null = null;
let posSeq = 0;
let lastPosSend = 0;
let mpActive = false;
let mpSessionMode: MpGameMode | null = null;
let localSpectator = false;
let localAlive = true;
let localDeathHandled = false;
let gameOverHandled = false;
const POS_SEND_MS = 16;

export function isMultiplayerActive(): boolean {
  return mpActive;
}

export function getMpGameMode(): MpGameMode | null {
  return mpSessionMode ?? mpClient.gameMode ?? mpClient.room?.selectedMode ?? null;
}

export function isMpWasdMode(): boolean {
  return getMpGameMode() === "wasd";
}

export function isMpHighwayMode(): boolean {
  return getMpGameMode() === "highway";
}

/** True while the local canvas is running a Highway multiplayer match. */
export function isMpHighwayPlaying(): boolean {
  if (!hooks) return false;
  if (!hooks.isPlaying() || hooks.getGameMode() !== "multiplayer") return false;
  return getMpGameMode() === "highway";
}

function ensureMpHighwayClientStarted(): void {
  if (!hooks || getMpGameMode() !== "highway") return;
  if (isMpHighwayClientActive() || mpClient.gameSeed == null) return;
  beginMpHighwayClient(mpClient.gameSeed);
  const spawn = mpHighwayWorldSpawn();
  hooks.setMouse(spawn.x, spawn.y);
}

function prepareMpHighwaySession(): void {
  if (!hooks || getMpGameMode() !== "highway") return;
  resetMpHighwayClient();
  hooks.prepareMpModeGame("highway", {
    speedrunLevel: mpClient.gameSpeedrunLevel,
    sandboxConfig: mpClient.gameSandboxConfig,
  });
  ensureMpHighwayClientStarted();
}

export function isMpSpeedrunMode(): boolean {
  return getMpGameMode() === "speedrun";
}

export function isMpSnakeMode(): boolean {
  return getMpGameMode() === "snake";
}

export function isMpBallDodgeMode(): boolean {
  const m = getMpGameMode();
  return m === "normal" || m === "wasd" || m === "sandbox" || m === "snake";
}

export function isMpSpectator(): boolean {
  return localSpectator;
}

export function isMpLocalAlive(): boolean {
  return localAlive;
}

/** Server is authoritative for ball-dodge modes; highway runs client checks on synced snapshot. */
export function shouldSkipLocalCollision(): boolean {
  if (!mpActive) return false;
  return isMpBallDodgeMode();
}

function syncLocalPlayerFlagsFromRoom(): void {
  if (!mpClient.playerId || !mpClient.room) return;
  const rp = mpClient.room.players.find((p) => p.id === mpClient.playerId);
  if (!rp) return;
  if (rp.isSpectator) {
    localSpectator = true;
    localAlive = false;
  } else {
    localSpectator = false;
    localAlive = rp.alive;
  }
}

function markLocalMpDeath(coopFail: boolean): void {
  if (localDeathHandled || !localAlive) return;
  localDeathHandled = true;
  localAlive = false;
  if (mpClient.gameSubMode === "ffa" || getMpGameMode() === "snake") {
    localSpectator = true;
    hooks?.setTopHudText("Spectating…");
    hooks?.clearLocalTrail?.();
  } else if (coopFail) {
    hooks?.setTopHudText("Team eliminated!");
  }
}

function handleLocalHighwayDeath(coopFail: boolean): void {
  if (localDeathHandled || !localAlive) return;
  mpClient.sendHighwayDeath();
  markLocalMpDeath(coopFail);
}

/** Recover when canvas is playing but MP runtime was stopped (desync). */
export function ensureMultiplayerGameRunning(): void {
  if (!hooks) return;
  const inMatch =
    mpClient.room?.phase === "playing" ||
    mpClient.room?.phase === "results" ||
    mpClient.room?.phase === "speedrun_results" ||
    (hooks.isPlaying() && hooks.getGameMode() === "multiplayer" && mpSessionMode != null);
  if (!inMatch) return;
  if (isMultiplayerActive()) return;
  if (!hooks.isPlaying()) {
    if (mpClient.room?.phase !== "playing") return;
    void startMultiplayerGame();
    return;
  }
  mpActive = true;
  localDeathHandled = false;
  gameOverHandled = false;
  syncLocalPlayerFlagsFromRoom();
  if (isMpHighwayMode()) prepareMpHighwaySession();
}

export function notifyMpHighwayCollision(): void {
  if (!hooks || !isMpHighwayClientActive() || getMpGameMode() !== "highway") return;
  if (!localAlive || localSpectator || localDeathHandled) return;
  const { w, h } = hooks.getCanvasSize();
  const mouse = hooks.getMouse();
  const cr = hooks.getCursorRadiusPx();
  if (mpHighwayCheckCollision(mouse.x, mouse.y, cr, w, h)) {
    handleLocalHighwayDeath(mpClient.gameSubMode === "coop");
  }
}

export function mpHighwayLocalCursorScreen(cw: number, ch: number): { x: number; y: number } {
  if (!hooks) return { x: 0, y: 0 };
  const mouse = hooks.getMouse();
  return mpHighwayCursorOnCanvas(cw, ch, mouse.x, mouse.y);
}

export function initMultiplayerGameRuntime(h: MpGameHooks): void {
  hooks = h;
}

export async function startMultiplayerGame(): Promise<void> {
  if (!hooks) return;
  const mode = mpClient.gameMode ?? mpClient.room?.selectedMode ?? null;
  if (!mode) return;
  if (!mpClient.room && mpClient.gameMode == null) return;

  if (mode === "soccer") {
    return;
  }
  mpSessionMode = mode;
  mpActive = true;
  localSpectator = false;
  localAlive = true;
  localDeathHandled = false;
  gameOverHandled = false;
  resetMpHighwayClient();
  mpSnapshotBuffer.clear();
  remotePlayers.clear();
  resetMpSpeedrunSpectate();
  resetMpSnakeTrails();
  posSeq = 0;
  lastPosSend = 0;

  hooks.initGame("multiplayer");
  if (mode === "highway") {
    prepareMpHighwaySession();
    const spawn = mpHighwayWorldSpawn();
    hooks.setMouse(spawn.x, spawn.y);
  } else {
    hooks.prepareMpModeGame(mode, {
      speedrunLevel: mpClient.gameSpeedrunLevel,
      sandboxConfig: mpClient.gameSandboxConfig,
    });
  }
  const { w, h } = hooks.getCanvasSize();
  if (mode !== "highway" && mode !== "speedrun") {
    hooks.setMouse(w / 2, h / 2);
  }
  if (localAlive && !localSpectator) {
    const norm = hooks.encodeMpPos(mode, hooks.getMouse(), w, h);
    mpClient.sendPos(norm.x, norm.y, posSeq++, hooks.getTrailEnabled(), hooks.getTrailColor());
    lastPosSend = performance.now();
  }
  hooks.setTopHudText(mpHudLabel());
  if (mode === "speedrun") {
    hooks.startMpGameCountdown();
  }
}

function mpHudLabel(): string {
  const mode = getMpGameMode();
  const sub = mpClient.gameSubMode;
  const modeLabel =
    mode === "normal"
      ? "NORMAL"
      : mode === "wasd"
      ? "WASD"
      : mode === "sandbox"
        ? "SANDBOX"
        : mode === "speedrun"
          ? "SPEEDRUN"
          : mode === "highway"
            ? "HIGHWAY"
            : mode === "snake"
              ? "SNAKE"
            : "MULTIPLAYER";
  if (mode === "speedrun" && sub === "ffa") return `${modeLabel} RACE`;
  if (mode === "speedrun" && sub === "coop") return `${modeLabel} CO-OP — finish together`;
  if (sub === "coop") return `${modeLabel} CO-OP — stay alive together`;
  if (sub === "ffa") return `${modeLabel} FFA — last one standing`;
  return modeLabel;
}

export function stopMultiplayerGame(): void {
  mpActive = false;
  mpSessionMode = null;
  localSpectator = false;
  localAlive = true;
  localDeathHandled = false;
  gameOverHandled = false;
  resetMpHighwayClient();
  setHighwayRandom(gameRandom);
  resetMpSpeedrunSpectate();
  resetMpSnakeTrails();
  mpSnapshotBuffer.clear();
  remotePlayers.clear();
}

export function releaseMultiplayerGamePointer(): void {
  hooks?.releaseGamePointer();
}

export function tickMultiplayer(dt: number): void {
  if (!hooks) return;
  if (!mpActive) {
    if (hooks.isPlaying() && hooks.getGameMode() === "multiplayer") {
      ensureMultiplayerGameRunning();
    }
    if (!mpActive) return;
  }

  const now = performance.now();
  const { w, h } = hooks.getCanvasSize();
  const mouse = hooks.getMouse();
  const mode = getMpGameMode() ?? "normal";

  if (localAlive && !localSpectator && !localDeathHandled) {
    const norm = hooks.encodeMpPos(mode, mouse, w, h);
    if (now - lastPosSend >= POS_SEND_MS && !hooks.isMpGameplayInputBlocked()) {
      lastPosSend = now;
      mpClient.sendPos(
        norm.x,
        norm.y,
        posSeq++,
        hooks.getTrailEnabled(),
        hooks.getTrailColor(),
      );
    }
  }

  remotePlayers.syncNames(playerNameMap());
  remotePlayers.syncCosmetics(playerCosmeticMap());

  const syncFrame = mpSnapshotBuffer.latestFrame() ?? mpSnapshotBuffer.sample(now);
  if (syncFrame) {
    for (const sp of syncFrame.players) {
      if (sp.speedrunPoints) setRemoteSpeedrunPoints(sp.id, sp.speedrunPoints);
    }
    remotePlayers.applyFrame(syncFrame, mpClient.playerId, dt);

    if (isMpSpeedrunCoop() && syncFrame.teamSpeedrunPoints) {
      applyCoopTeamSpeedrunPointsFromServer(syncFrame.teamSpeedrunPoints);
    }

    const localState = syncFrame.players.find((p) => p.id === mpClient.playerId);
    if (localState && !localState.alive && localAlive && !localDeathHandled && (isMpHighwayMode() || isMpSnakeMode())) {
      markLocalMpDeath(mpClient.gameSubMode === "coop");
    }
  }

  if (isMpHighwayMode()) {
    ensureMpHighwayClientStarted();
    const serverScore = syncFrame?.score;
    tickMpHighwayClient(
      dt,
      serverScore != null && Number.isFinite(serverScore) ? serverScore : undefined,
      syncFrame?.highway,
    );

    if (localAlive && !localSpectator && !localDeathHandled) {
      const cr = hooks.getCursorRadiusPx();
      const mouse = hooks.getMouse();
      if (mpHighwayCheckCollision(mouse.x, mouse.y, cr, w, h)) {
        handleLocalHighwayDeath(mpClient.gameSubMode === "coop");
      }
    }
  }

  if (isMpSnakeMode() && localAlive && !localSpectator && !localDeathHandled && mpClient.playerId) {
    const norm = hooks.encodeMpPos("snake", mouse, w, h);
    tickLocalMpSnakeTrail(mpClient.playerId, norm.x, norm.y, hooks.getTrailColor());
  }
}

export function renderMultiplayerTrails(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
): void {
  if ((!mpActive && !isMpHighwayPlaying()) || !shouldDrawRemoteMpTrails()) return;
  if (isMpSnakeMode()) return;
  remotePlayers.drawRemoteTrails(ctx, cw, ch, mpClient.playerId, isMpHighwayMode());
}

export function renderMpSnakeTrails(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  layoutScale: number,
  fallbackColor: string,
): void {
  if (!hooks?.isPlaying() || hooks.getGameMode() !== "multiplayer") return;
  if (!isMpSnakeMode()) return;
  drawMpSnakeTrails(ctx, cw, ch, layoutScale, fallbackColor);
}

export function renderMpSnakePlayBorder(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  layoutScale: number,
  color: string,
): void {
  if (!hooks?.isPlaying() || hooks.getGameMode() !== "multiplayer") return;
  if (!isMpSnakeMode()) return;
  drawMpSnakePlayBorder(ctx, cw, ch, color, layoutScale);
}

export function renderMultiplayerCursors(
  ctxTop: CanvasRenderingContext2D | null,
  cw: number,
  ch: number,
  cursorRadiusPx: number,
  _localCursorX: number,
  _localCursorY: number,
): void {
  if (!ctxTop || !hooks) return;
  if (!mpActive && !isMpHighwayPlaying()) return;

  if (shouldDrawRemoteMpCursors()) {
    remotePlayers.drawRemoteCursors(ctxTop, cw, ch, mpClient.playerId, cursorRadiusPx, isMpHighwayMode());
  }

  if (localAlive && !localSpectator && !shouldHideMpLocalCursor()) {
    const local = hooks.getMpLocalCursor(cw, ch);
    ctxTop.save();
    if (!equippedHidesBaseCursor()) {
      ctxTop.fillStyle = "#ffffff";
      ctxTop.beginPath();
      ctxTop.arc(local.x, local.y, cursorRadiusPx, 0, Math.PI * 2);
      ctxTop.fill();
    }
    drawEquippedCosmeticOverlays(ctxTop, local.x, local.y, cursorRadiusPx);
    ctxTop.restore();
  } else if (isMpSpeedrunSpectating()) {
    const sp = getMpSpectateCursorCanvas(cw, ch);
    if (sp) {
      ctxTop.save();
      ctxTop.fillStyle = "#ffffff";
      ctxTop.beginPath();
      ctxTop.arc(sp.x, sp.y, cursorRadiusPx, 0, Math.PI * 2);
      ctxTop.fill();
      ctxTop.restore();
    }
  }
}

export function renderMpHighwayLayer(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  accentHex: string,
  nowMs: number,
): void {
  if (!isMpHighwayClientActive()) return;
  drawMpHighwayClient(ctx, cw, ch, accentHex, nowMs);
}

export function renderMultiplayerObstacles(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  ballsColor: string,
): void {
  if (!mpActive || !isMpBallDodgeMode()) return;
  remotePlayers.drawObstacles(ctx, cw, ch, ballsColor);
}

export function handleMpServerEvent(
  msg: {
    type: string;
    playerId?: string;
    coopFail?: boolean;
    message?: string;
    id?: string;
    name?: string;
    timeMs?: number;
    teamPoints?: number[];
    mode?: MpGameMode;
    seed?: number;
  },
): void {
  const inMpSession = mpActive || (hooks?.isPlaying() && hooks?.getGameMode() === "multiplayer");
  if (!inMpSession) return;

  if (msg.type === "game_start" && msg.mode === "highway" && msg.seed != null) {
    beginMpHighwayClient(msg.seed);
  }

  if (msg.type === "player_died" && msg.playerId === mpClient.playerId) {
    if (!mpActive) return;
    markLocalMpDeath(!!msg.coopFail);
  }

  if (msg.type === "speedrun_coop_respawn") {
    respawnMpSpeedrunFromLaser();
    applyTeamSpeedrunPoints(msg.teamPoints ?? []);
  }

  if (msg.type === "speedrun_finisher" && msg.id && msg.name != null && msg.timeMs != null) {
    addMpSpeedrunFinisher({ id: msg.id, name: msg.name, timeMs: msg.timeMs });
  }
}

export function onLocalMpDeathBegin(): boolean {
  if (!mpActive) return false;
  return isMpBallDodgeMode();
}

export function getMpScore(): number {
  if (isMpHighwayMode() && isMpHighwayClientActive()) {
    return Math.floor(mpHighwayClientScore());
  }
  return remotePlayers.serverScore;
}

function playerNameMap(): Map<string, string> {
  const m = new Map<string, string>();
  for (const p of mpClient.room?.players ?? []) {
    m.set(p.id, p.name);
  }
  return m;
}

function playerCosmeticMap(): Map<string, string[]> {
  const m = new Map<string, string[]>();
  for (const p of mpClient.room?.players ?? []) {
    const ids = filterPlayCosmeticIds(p.equippedCosmeticIds);
    if (ids.length) m.set(p.id, ids);
  }
  return m;
}

export async function ensureMpConnected(): Promise<void> {
  if (mpClient.playerId && mpClient.connected) return;
  await mpClient.connect();
}

export { mpClient, remotePlayers, mpSnapshotBuffer };
